import { __decorate } from "tslib";
import { ArrayDecoder, field } from '@simonbackx/simple-encoding';
import { ChoicesFilterChoice, ChoicesFilterDefinition, ChoicesFilterMode } from '../filters/ChoicesFilter';
import { DateFilterDefinition } from '../filters/DateFilter';
import { NumberFilterDefinition } from '../filters/NumberFilter';
import { RegistrationsFilterChoice, RegistrationsFilterDefinition } from '../filters/RegistrationsFilter';
import { StringFilterDefinition } from '../filters/StringFilter';
import { Group } from '../Group';
import { UmbrellaOrganization } from '../UmbrellaOrganization';
import { RegisterCartValidator } from './checkout/RegisterCartValidator';
import { EncryptedMemberWithRegistrations } from './EncryptedMemberWithRegistrations';
import { Gender } from './Gender';
import { MemberDetailsWithGroups } from './OrganizationRecordsConfiguration';
import { RecordType } from './records/RecordSettings';
import { Registration } from './Registration';
export class MemberWithRegistrations extends EncryptedMemberWithRegistrations {
    constructor() {
        super(...arguments);
        // Calculated properties for convenience
        this.activeRegistrations = [];
        /**
         * Groups the member is currently registered for
         */
        this.groups = [];
        /**
         * Groups the member is on the waiting list for (not accepted)
         */
        this.waitingGroups = [];
        /**
         * Groups the member is on the waiting list for and is accepted for
         */
        this.acceptedWaitingGroups = [];
        /**
         * All groups of this organization (used for finding information of groups)
         */
        this.allGroups = [];
    }
    get inactiveRegistrations() {
        return this.registrations.filter(r => !!this.activeRegistrations.find(r2 => r2.id == r.id));
    }
    get isMinor() {
        return (this.details.age == null && !!this.groups.find(g => g.settings.maxAge !== null && g.settings.maxAge < 18)) || super.isMinor;
    }
    /**
     * Return true if this member was registered in the previous year (current doesn't count)
     */
    get isExistingMember() {
        return RegisterCartValidator.isExistingMember(this, this.allGroups);
    }
    static fromMember(member, groups) {
        const m = MemberWithRegistrations.create(member);
        m.fillGroups(groups);
        return m;
    }
    filterRegistrations(filters) {
        return this.registrations.filter(r => {
            var _a, _b;
            if (filters.groups && !filters.groups.find(g => g.id === r.groupId)) {
                return false;
            }
            let cycle = filters.cycle;
            if (filters.cycle === undefined) {
                const group = ((_a = filters.groups) !== null && _a !== void 0 ? _a : this.allGroups).find(g => g.id === r.groupId);
                if (group) {
                    cycle = group.cycle - ((_b = filters.cycleOffset) !== null && _b !== void 0 ? _b : 0);
                }
            }
            if (cycle !== undefined
                && (filters.waitingList === undefined || r.waitingList === filters.waitingList)
                && r.cycle === cycle) {
                if (filters.canRegister !== undefined && r.waitingList) {
                    return r.canRegister === filters.canRegister;
                }
                return true;
            }
            return false;
        });
    }
    get syncStatus() {
        if (!this.details.lastExternalSync) {
            return 'never';
        }
        for (const registration of this.filterRegistrations({ cycleOffset: 0, waitingList: false })) {
            if (registration.registeredAt && registration.registeredAt > this.details.lastExternalSync) {
                return 'outdated';
            }
        }
        if (this.details.lastExternalSync.getTime() < new Date().getTime() - 1000 * 60 * 60 * 24 * 30 * 9) {
            return 'outdated';
        }
        if (Math.abs(this.details.lastExternalSync.getTime() - this.updatedAt.getTime()) < 1000 * 5) {
            return 'ok';
        }
        return 'changed';
    }
    /**
     * Pass all the groups of an organization to the member so we can fill in all the groups and registrations that are active
     */
    fillGroups(groups) {
        this.activeRegistrations = [];
        const groupMap = new Map();
        const waitlistGroups = new Map();
        const acceptedWaitlistGroups = new Map();
        for (const registration of this.registrations) {
            const group = groups.find(g => g.id == registration.groupId);
            if (group) {
                if (group.cycle == registration.cycle && registration.deactivatedAt === null && (registration.registeredAt !== null || registration.waitingList)) {
                    this.activeRegistrations.push(registration);
                    if (!registration.waitingList) {
                        groupMap.set(group.id, group);
                    }
                    else {
                        if (registration.canRegister) {
                            acceptedWaitlistGroups.set(group.id, group);
                        }
                        else {
                            waitlistGroups.set(group.id, group);
                        }
                    }
                }
            }
            else {
                console.error("Group not found", registration.groupId);
            }
        }
        this.groups = Array.from(groupMap.values());
        this.waitingGroups = Array.from(waitlistGroups.values());
        this.acceptedWaitingGroups = Array.from(acceptedWaitlistGroups.values());
        this.allGroups = groups.slice();
    }
    getAllEmails() {
        const emails = new Set(this.details.getAllEmails());
        for (const user of this.users) {
            if (user.email) {
                emails.add(user.email);
            }
        }
        return [...emails];
    }
    canRegister(group, family, categories, cart) {
        return RegisterCartValidator.canRegister(this, group, family, this.allGroups, categories, cart);
    }
    /**
     * Instead of listening for changes to a member, editing components can push changes to existing instances
     */
    copyFrom(member) {
        this.details = member.details;
        this.activeRegistrations = member.activeRegistrations;
        this.waitingGroups = member.waitingGroups;
        this.acceptedWaitingGroups = member.acceptedWaitingGroups;
        this.allGroups = member.allGroups;
        if (member.groups !== this.groups) {
            this.groups.splice(0, this.groups.length, ...member.groups);
        }
        this.registrations = member.registrations;
        if (member.users !== this.users) {
            this.users.splice(0, this.users.length, ...member.users);
        }
    }
    static getBaseFilterDefinitions(organization) {
        return [
            new StringFilterDefinition({
                id: "member_name",
                name: "Naam lid",
                getValue: (member) => {
                    var _a;
                    return (_a = member === null || member === void 0 ? void 0 : member.name) !== null && _a !== void 0 ? _a : "";
                }
            }),
            new RegistrationsFilterDefinition({
                id: "registrations",
                name: "Inschrijvingen",
                getValue: (member) => {
                    var _a, _b;
                    const groups = (_a = member.groups.map(g => RegistrationsFilterChoice.create({
                        id: g.id,
                        name: g.settings.name,
                        waitingList: false
                    }))) !== null && _a !== void 0 ? _a : [];
                    const waitingGroups = (_b = member.waitingGroups.map(g => RegistrationsFilterChoice.create({
                        id: g.id,
                        name: g.settings.name,
                        waitingList: true
                    }))) !== null && _b !== void 0 ? _b : [];
                    return [...groups, ...waitingGroups];
                }
            }),
            new NumberFilterDefinition({
                id: "member_age",
                name: "Leeftijd",
                getValue: (member) => {
                    var _a;
                    return (_a = member.details.age) !== null && _a !== void 0 ? _a : 99;
                },
                floatingPoint: false
            }),
            new DateFilterDefinition({
                id: "member_birthDay",
                name: "Geboortedatum",
                getValue: (member) => {
                    var _a;
                    return (_a = member.details.birthDay) !== null && _a !== void 0 ? _a : new Date(1900, 0, 1);
                },
                time: false
            }),
            new ChoicesFilterDefinition({
                id: "gender",
                name: "Geslacht",
                choices: [
                    new ChoicesFilterChoice(Gender.Male, "Man"),
                    new ChoicesFilterChoice(Gender.Female, "Vrouw"),
                    new ChoicesFilterChoice(Gender.Other, "Andere"),
                ],
                getValue: (member) => {
                    return [member.details.gender];
                },
                defaultMode: ChoicesFilterMode.Or
            }),
            new ChoicesFilterDefinition({
                id: "member_missing_data",
                name: "Ontbrekende gegevens",
                description: "Toon leden als één van de geselecteerde gegevens ontbreekt of niet is ingevuld.",
                choices: [
                    ...(organization.meta.umbrellaOrganization === UmbrellaOrganization.ScoutsEnGidsenVlaanderen ? [new ChoicesFilterChoice("memberNumber", "Lidnummer")] : []),
                    new ChoicesFilterChoice("birthDay", "Geboortedatum"),
                    new ChoicesFilterChoice("address", "Adres", "Van lid zelf"),
                    new ChoicesFilterChoice("phone", "Telefoonnummer", "Van lid zelf"),
                    new ChoicesFilterChoice("email", "E-mailadres", "Van lid zelf"),
                    new ChoicesFilterChoice("parents", "Ouders"),
                    new ChoicesFilterChoice("secondParent", "Tweede ouder", "Als er maar één ouder is toegevoegd aan een lid. Handig om te selecteren op een eenoudergezin."),
                    new ChoicesFilterChoice("emergencyContact", "Noodcontact"),
                    ...organization.meta.recordsConfiguration.recordCategories.flatMap(c => c.childCategories.length > 0 ? c.childCategories : [c]).map(category => {
                        return new ChoicesFilterChoice("record-category-" + category.id, category.name);
                    })
                ],
                getValue: (member) => {
                    var _a, _b;
                    const missing = [];
                    if (!member.details.memberNumber) {
                        missing.push("memberNumber");
                    }
                    if (!member.details.birthDay) {
                        missing.push("birthDay");
                    }
                    if (!member.details.address) {
                        missing.push("address");
                    }
                    if (!member.details.phone) {
                        missing.push("phone");
                    }
                    if (!member.details.email) {
                        missing.push("email");
                    }
                    if (member.details.parents.length == 0) {
                        missing.push("parents");
                    }
                    if (member.details.parents.length == 1) {
                        missing.push("secondParent");
                    }
                    if (member.details.emergencyContacts.length == 0) {
                        missing.push("emergencyContact");
                    }
                    const categories = organization.meta.recordsConfiguration.recordCategories.flatMap(c => c.childCategories.length > 0 ? c.childCategories : [c]);
                    const m = new MemberDetailsWithGroups(member.details, member, []);
                    for (const category of categories) {
                        const records = category.getAllFilteredRecords(m, MemberDetailsWithGroups.getBaseFilterDefinitions(), (_b = (_a = member.details.dataPermissions) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : false);
                        const missingRecord = records.find(r => (r.required || r.type === RecordType.Checkbox || records.length == 1) && !member.details.recordAnswers.find(a => a.settings.id === r.id));
                        if (missingRecord) {
                            missing.push("record-category-" + category.id);
                        }
                    }
                    return missing;
                },
                defaultMode: ChoicesFilterMode.Or
            }),
            new ChoicesFilterDefinition({
                id: "members_accounts",
                name: "Accounts",
                description: "Filter leden die wel of geen account hebben om de gegevens van leden te wijzigen via het ledenportaal.",
                choices: [
                    new ChoicesFilterChoice("no_account", "Heeft geen account"),
                    new ChoicesFilterChoice("has_account", "Heeft een account")
                ],
                getValue: (member) => {
                    const missing = [];
                    if (member.users.find(u => u.hasAccount)) {
                        missing.push("has_account");
                    }
                    else {
                        missing.push("no_account");
                    }
                    return missing;
                },
                defaultMode: ChoicesFilterMode.Or
            }),
            new NumberFilterDefinition({
                id: "outstandingBalance",
                name: "Openstaand saldo",
                getValue: (member) => {
                    return member.outstandingBalance;
                },
                currency: true
            }),
            new ChoicesFilterDefinition({
                id: "financial_support",
                name: "Financiële ondersteuning",
                choices: [
                    new ChoicesFilterChoice("checked", "Vroeg financiële ondersteuning aan"),
                    new ChoicesFilterChoice("not_checked", "Geen financiële ondersteuning"),
                ],
                getValue: (member) => {
                    var _a;
                    // TODO: remove spaces
                    if ((_a = member.details.requiresFinancialSupport) === null || _a === void 0 ? void 0 : _a.value) {
                        return ["checked"];
                    }
                    return ["not_checked"];
                }
            }),
            new ChoicesFilterDefinition({
                id: "data_permissions",
                name: "Toestemming gegevensverzameling",
                choices: [
                    new ChoicesFilterChoice("checked", "Gaf toestemming"),
                    new ChoicesFilterChoice("not_checked", "Gaf geen toestemming"),
                ],
                getValue: (member) => {
                    var _a;
                    // TODO: remove spaces
                    if ((_a = member.details.dataPermissions) === null || _a === void 0 ? void 0 : _a.value) {
                        return ["checked"];
                    }
                    return ["not_checked"];
                }
            })
        ];
    }
    matchQuery(q) {
        return this.details.matchQuery(q);
    }
}
__decorate([
    field({ decoder: new ArrayDecoder(Registration), optional: true })
], MemberWithRegistrations.prototype, "activeRegistrations", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), optional: true })
], MemberWithRegistrations.prototype, "groups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), optional: true })
], MemberWithRegistrations.prototype, "waitingGroups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), optional: true })
], MemberWithRegistrations.prototype, "acceptedWaitingGroups", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Group), optional: true })
], MemberWithRegistrations.prototype, "allGroups", void 0);
//# sourceMappingURL=MemberWithRegistrations.js.map